import os from "os";
import path from "path";
import fs from "fs";
import { RepairRule } from "../repairEngine.js";
import { findPackageJson, directoryExists } from "../../utils/files.js";
import { runCommand } from "../../utils/exec.js";

// Helper to expand ~ to home directory safely
function getHomeDir(): string {
  return os.homedir();
}

export const cacheRepairRules: RepairRule[] = [
  {
    id: "metro-cache-clean",
    title: "Clean Metro Cache",
    description: "Clears Metro bundler cache by deleting local package caches and temp directories.",
    safe: true,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;
      const root = path.dirname(pPath);
      const cachePath = path.join(root, "node_modules", ".cache");
      return fs.existsSync(cachePath) || fs.existsSync(path.join(os.tmpdir(), "metro-cache"));
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return { success: false, details: "package.json not found." };
      const root = path.dirname(pPath);

      let count = 0;
      // 1. Delete node_modules/.cache
      const cachePath = path.join(root, "node_modules", ".cache");
      if (fs.existsSync(cachePath)) {
        try {
          fs.rmSync(cachePath, { recursive: true, force: true });
          count++;
        } catch {}
      }

      // 2. Clear temp metro-cache directories
      const tmpDir = os.tmpdir();
      try {
        const files = fs.readdirSync(tmpDir);
        for (const file of files) {
          if (file.startsWith("metro-") || file.startsWith("react-native-")) {
            const filePath = path.join(tmpDir, file);
            try {
              const stat = fs.statSync(filePath);
              if (stat.isDirectory()) {
                fs.rmSync(filePath, { recursive: true, force: true });
                count++;
              } else {
                fs.unlinkSync(filePath);
                count++;
              }
            } catch {}
          }
        }
      } catch {}

      return { success: true, details: `Cleared Metro caches (cleaned ${count} files/directories).` };
    },
  },
  {
    id: "watchman-cache-clean",
    title: "Clear Watchman File Watchers",
    description: "Runs watchman watch-del-all and shuts down the watchman server to clear file watcher locks.",
    safe: true,
    async detect(cwd: string): Promise<boolean> {
      // Check if watchman exists in PATH
      const result = await runCommand("watchman --version");
      return !result.error;
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const delAll = await runCommand("watchman watch-del-all");
      const shutdown = await runCommand("watchman shutdown-server");

      if (delAll.error) {
        return {
          success: false,
          details: `Watchman clean failed: ${delAll.stderr || delAll.stdout}`,
        };
      }
      return { success: true, details: "Watchman watcher state cleared." };
    },
  },
  {
    id: "derived-data-clean",
    title: "Clean Xcode Derived Data",
    description: "Cleans Xcode DerivedData caches on macOS to solve indexing or linker issues.",
    safe: false,
    async detect(cwd: string): Promise<boolean> {
      if (os.platform() !== "darwin") return false;
      const derivedDataPath = path.join(getHomeDir(), "Library", "Developer", "Xcode", "DerivedData");
      return directoryExists(derivedDataPath);
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const derivedDataPath = path.join(getHomeDir(), "Library", "Developer", "Xcode", "DerivedData");
      if (!directoryExists(derivedDataPath)) {
        return { success: true, details: "DerivedData does not exist." };
      }

      try {
        const subdirs = fs.readdirSync(derivedDataPath);
        for (const subdir of subdirs) {
          const fullPath = path.join(derivedDataPath, subdir);
          fs.rmSync(fullPath, { recursive: true, force: true });
        }
        return { success: true, details: "Xcode DerivedData cleaned successfully." };
      } catch (err: any) {
        return {
          success: false,
          details: `DerivedData clean encountered errors: ${err?.message || "Unknown error"}`,
        };
      }
    },
  },
  {
    id: "temp-build-clean",
    title: "Remove iOS Temporary Build Folders",
    description: "Removes native iOS project build folders (ios/build) to resolve cached product compilation conflicts.",
    safe: true,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;
      const root = path.dirname(pPath);
      const iosBuild = path.join(root, "ios", "build");
      return directoryExists(iosBuild);
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return { success: false, details: "package.json not found." };
      const root = path.dirname(pPath);
      const iosBuild = path.join(root, "ios", "build");

      if (directoryExists(iosBuild)) {
        try {
          fs.rmSync(iosBuild, { recursive: true, force: true });
          return { success: true, details: "Removed ios/build directory successfully." };
        } catch (err: any) {
          return { success: false, details: `Failed to remove ios/build: ${err?.message || "Unknown error"}` };
        }
      }
      return { success: true, details: "ios/build directory was already clean." };
    },
  },
];
